// -----------------------------------------------------------
// patPatternDetector.js
// Total, topology-only causal pattern classifier
// -----------------------------------------------------------

export function detectCausalPattern(nodes = [], edges = []) {
  try {
    // Defensive normalisation
    const nodeIds = nodes.map((n) => n?.id).filter(Boolean);
    const edgePairs = Array.isArray(edges) ? edges : [];

    // Build degree tables
    const inDeg = {};
    const outDeg = {};

    nodeIds.forEach((id) => {
      inDeg[id] = 0;
      outDeg[id] = 0;
    });

    edgePairs.forEach(([s, t]) => {
      if (s in outDeg) outDeg[s] += 1;
      if (t in inDeg) inDeg[t] += 1;
    });

    // -----------------------------------------------------------
    // Guard: insufficient structure for classification
    // -----------------------------------------------------------
    if (edgePairs.length < 2) {
      return {
        type: "insufficient",
        label: "Additional relationships are required to identify a pattern",
        confidence: null,
        explanation:
          "Add more causal relationships if you want the system to describe the overall structure.",
      };
    }

    // Identify sinks (possible outcomes)
    const sinks = nodeIds.filter((id) => outDeg[id] === 0);
    const sources = nodeIds.filter((id) => inDeg[id] === 0);

    // -----------------------------------------------------------
    // Parallel causes: many sources → one sink, no other edges
    // -----------------------------------------------------------
    if (
      sinks.length === 1 &&
      sources.length > 1 &&
      edgePairs.every(([s, t]) => t === sinks[0])
    ) {
      return {
        type: "parallel",
        label: "Independent parallel causes",
        confidence: 0.9,
        explanation:
          "Multiple variables independently influence a single outcome, with no mediated paths.",
      };
    }

    // -----------------------------------------------------------
    // Linear chain: exactly one source, one sink, rest degree-1
    // -----------------------------------------------------------
    const middle = nodeIds.filter((id) => inDeg[id] === 1 && outDeg[id] === 1);

    if (
      sources.length === 1 &&
      sinks.length === 1 &&
      middle.length === nodeIds.length - 2
    ) {
      return {
        type: "chain",
        label: "Linear mediated chain",
        confidence: 0.85,
        explanation:
          "Variables form a sequential causal chain, indicating mediation.",
      };
    }

    // -----------------------------------------------------------
    // Collider: many parents, no outgoing edges from parents
    // -----------------------------------------------------------
    if (
      sinks.length === 1 &&
      inDeg[sinks[0]] > 1 &&
      nodeIds.every((id) => id === sinks[0] || outDeg[id] === 1)
    ) {
      return {
        type: "collider",
        label: "Collider structure",
        confidence: 0.75,
        explanation:
          "Multiple variables converge on a single outcome without interacting upstream.",
      };
    }

    // -----------------------------------------------------------
    // Fork: one source influences multiple downstream nodes
    // -----------------------------------------------------------
    const forks = nodeIds.filter((id) => outDeg[id] > 1);

    if (forks.length > 0) {
      return {
        type: "fork",
        label: "Common-cause fork",
        confidence: 0.65,
        explanation:
          "At least one variable influences multiple downstream variables, suggesting confounding.",
      };
    }

    // -----------------------------------------------------------
    // Fallback: hybrid / unclear
    // -----------------------------------------------------------
    return {
      type: "unclear",
      label: "Structure does not match a standard causal pattern",
      confidence: 0.35,
      explanation:
        "The diagram contains several causal relationships that do not form a single simple pattern. This is common in real systems and does not affect the validity of the model.",
    };
  } catch (err) {
    // Absolute safety net — should never be reached
    return {
      type: "unclear",
      label: "Unclassifiable structure",
      confidence: null,
      explanation:
        "The causal structure could not be classified, but remains valid for analysis.",
    };
  }
}
